import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Calendar, Clock, Users } from "lucide-react";

const WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

const emptyTemplate = {
  branch_id: "",
  name: "",
  start_time: "08:00",
  end_time: "16:00",
  pay_multiplier: 100,
  max_slots: 1,
  weekday_mask: 127,
  role_filter: "",
  active: true,
};

function maskToDays(m) {
  return WEEKDAYS.map((_, i) => !!(m & (1 << i)));
}
function daysToMask(days) {
  return days.reduce((acc, on, i) => (on ? acc | (1 << i) : acc), 0);
}

export default function Shifts() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [filterBranch, setFilterBranch] = useState("__all__");
  const [editing, setEditing] = useState(null);
  const [ovEditing, setOvEditing] = useState(null);
  const today = new Date();
  const isoToday = today.toISOString().slice(0, 10);
  const isoPlus = (n) => new Date(today.getTime() + n * 86400000).toISOString().slice(0, 10);
  const [regFrom, setRegFrom] = useState(isoPlus(-7));
  const [regTo, setRegTo] = useState(isoPlus(14));

  const loadTemplates = () => api.get("/shifts/templates").then((r) => setTemplates(r.data));
  const loadBranches = () => api.get("/branches").then((r) => setBranches(r.data));
  const loadOverrides = () =>
    api.get(`/shifts/day-overrides?from_date=${isoPlus(-30)}&to_date=${isoPlus(60)}`).then((r) => setOverrides(r.data));
  const loadRegs = useCallback(() =>
    api.get(`/shifts/registrations?from_date=${regFrom}&to_date=${regTo}`).then((r) => setRegistrations(r.data)), [regFrom, regTo]);

  useEffect(() => {
    loadBranches();
    loadTemplates();
    loadOverrides();
    loadRegs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRegs(); }, [loadRegs]);

  const visibleTemplates = useMemo(() => {
    return filterBranch === "__all__" ? templates : templates.filter((t) => t.branch_id === filterBranch);
  }, [templates, filterBranch]);

  const saveTemplate = async () => {
    if (!editing.branch_id) return toast.error("Chọn chi nhánh");
    if (!editing.name) return toast.error("Nhập tên ca");
    const payload = {
      ...editing,
      pay_multiplier: Number(editing.pay_multiplier) || 100,
      max_slots: Number(editing.max_slots) || 1,
      role_filter: editing.role_filter || null,
    };
    try {
      if (editing.id) {
        await api.put(`/shifts/templates/${editing.id}`, payload);
        toast.success("Đã cập nhật ca");
      } else {
        await api.post("/shifts/templates", payload);
        toast.success("Đã tạo ca");
      }
      setEditing(null);
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không lưu được");
    }
  };

  const removeTemplate = async (id) => {
    try {
      await api.delete(`/shifts/templates/${id}`);
      toast.success("Đã xóa");
      loadTemplates();
      loadRegs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không xóa được");
    }
  };

  const saveOverride = async () => {
    if (!ovEditing.work_date) return toast.error("Chọn ngày");
    try {
      await api.post("/shifts/day-overrides", {
        work_date: ovEditing.work_date,
        pay_multiplier: Number(ovEditing.pay_multiplier) || 100,
        reason: ovEditing.reason || null,
      });
      toast.success("Đã lưu hệ số ngày");
      setOvEditing(null);
      loadOverrides();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không lưu được");
    }
  };

  const removeOverride = async (id) => {
    await api.delete(`/shifts/day-overrides/${id}`);
    loadOverrides();
  };

  const cancelReg = async (id) => {
    try {
      await api.delete(`/shifts/registrations/${id}`);
      toast.success("Đã hủy");
      loadRegs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không hủy được");
    }
  };

  return (
    <div data-testid="page-shifts">
      <PageHeader
        kicker="HR-Ops"
        title="Quản lý ca làm"
        breadcrumb={["HR-Ops", "Ca làm"]}
        actions={
          tab === "templates" ? (
            <Button onClick={() => setEditing({ ...emptyTemplate })} className="bg-emerald-900 text-white rounded-sm" data-testid="btn-new-template">
              <Plus className="w-4 h-4 mr-1" /> Khung ca mới
            </Button>
          ) : tab === "overrides" ? (
            <Button onClick={() => setOvEditing({ work_date: isoToday, pay_multiplier: 150, reason: "" })} className="bg-emerald-900 text-white rounded-sm" data-testid="btn-new-override">
              <Plus className="w-4 h-4 mr-1" /> Hệ số ngày
            </Button>
          ) : null
        }
      />
      <div className="p-6 lg:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-stone-100 rounded-sm">
            <TabsTrigger value="templates" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="tab-templates">
              Khung ca ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="regs" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="tab-regs">
              Đăng ký ca ({registrations.length})
            </TabsTrigger>
            <TabsTrigger value="overrides" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="tab-overrides">
              Hệ số ngày ({overrides.length})
            </TabsTrigger>
          </TabsList>

          {/* TEMPLATES */}
          <TabsContent value="templates" className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Chi nhánh</Label>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger className="w-64 rounded-sm h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tất cả</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["Chi nhánh", "Tên ca", "Bắt đầu", "Kết thúc", "Hệ số lương", "Slot", "Ngày trong tuần", "Trạng thái", ""].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {visibleTemplates.map((t) => (
                    <tr key={t.id} className="hover:bg-stone-50" data-testid={`row-template-${t.id}`}>
                      <td className="px-4 py-2 text-xs text-stone-600">{t.branch_name}</td>
                      <td className="px-4 py-2 font-semibold">{t.name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{t.start_time}</td>
                      <td className="px-4 py-2 font-mono text-xs">{t.end_time}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        <span className={`px-1.5 py-0.5 rounded-sm font-semibold ${
                          Number(t.pay_multiplier) >= 200 ? "bg-amber-100 text-amber-900" :
                          Number(t.pay_multiplier) >= 150 ? "bg-emerald-100 text-emerald-900" :
                          "bg-stone-100 text-stone-700"}`}>{Number(t.pay_multiplier)}%</span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{t.max_slots}</td>
                      <td className="px-4 py-2 text-xs">
                        {maskToDays(t.weekday_mask).map((on, i) => (
                          <span key={i} className={`inline-block w-6 mr-0.5 text-center rounded-sm ${on ? "bg-emerald-900 text-white font-bold" : "bg-stone-100 text-stone-400"}`}>{WEEKDAYS[i]}</span>
                        ))}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${t.active ? "bg-emerald-100 text-emerald-900" : "bg-stone-100 text-stone-500"}`}>
                          {t.active ? "Đang dùng" : "Tắt"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <Button onClick={() => setEditing(t)} size="sm" variant="outline" className="rounded-sm h-7 mr-1" data-testid={`btn-edit-${t.id}`}><Pencil className="w-3 h-3" /></Button>
                        <DestructiveConfirmDialog
                          title="Xóa khung ca?"
                          description={`Khung ca ${t.name || t.id} và các đăng ký liên quan sẽ bị xóa.`}
                          confirmLabel="Xóa khung ca"
                          token={String(t.name || t.id)}
                          onConfirm={() => removeTemplate(t.id)}
                          trigger={(
                            <Button size="sm" variant="outline" className="rounded-sm h-7 text-red-700" data-testid={`btn-del-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                          )}
                        />
                      </td>
                    </tr>
                  ))}
                  {visibleTemplates.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400">Chưa có khung ca</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REGISTRATIONS */}
          <TabsContent value="regs" className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Từ</Label>
              <Input type="date" value={regFrom} onChange={(e) => setRegFrom(e.target.value)} className="w-40 h-9 rounded-sm text-sm" />
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Đến</Label>
              <Input type="date" value={regTo} onChange={(e) => setRegTo(e.target.value)} className="w-40 h-9 rounded-sm text-sm" />
            </div>
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["Ngày", "Ca", "Giờ", "Chi nhánh", "Mã NV", "Họ tên", "Hệ số", "Trạng thái", ""].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {registrations.map((r) => (
                    <tr key={r.id} className="hover:bg-stone-50">
                      <td className="px-4 py-2 font-mono text-xs">{fmtDate(r.work_date)}</td>
                      <td className="px-4 py-2 font-semibold">{r.template_name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.start_time}–{r.end_time}</td>
                      <td className="px-4 py-2 text-stone-600 text-xs">{r.branch_name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-stone-600">{r.employee_code}</td>
                      <td className="px-4 py-2 font-semibold">{r.employee_name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{Number(r.pay_multiplier)}%</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                          r.status === "confirmed" ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-900"}`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {r.status === "confirmed" && (
                          <DestructiveConfirmDialog
                            title="Hủy đăng ký ca?"
                            description={`Đăng ký của ${r.employee_name || r.employee_code || r.id} cho ca ${r.template_name || r.work_date} sẽ bị hủy.`}
                            confirmLabel="Hủy đăng ký"
                            token={String(r.employee_code || r.employee_name || r.id)}
                            onConfirm={() => cancelReg(r.id)}
                            trigger={(
                              <Button size="sm" variant="outline" className="rounded-sm h-7 text-red-700" data-testid={`btn-cancel-${r.id}`}>
                                <X className="w-3 h-3 mr-1" />Hủy
                              </Button>
                            )}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                  {registrations.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400">Chưa có đăng ký</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* OVERRIDES */}
          <TabsContent value="overrides" className="mt-4">
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["Ngày", "Hệ số ×", "Lý do", ""].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {overrides.map((o) => (
                    <tr key={o.id} className="hover:bg-stone-50">
                      <td className="px-4 py-2 font-mono text-xs">{fmtDate(o.work_date)}</td>
                      <td className="px-4 py-2"><span className="px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 font-mono font-semibold text-xs">×{Number(o.pay_multiplier)}%</span></td>
                      <td className="px-4 py-2 text-stone-600">{o.reason || "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <Button onClick={() => setOvEditing(o)} size="sm" variant="outline" className="rounded-sm h-7 mr-1"><Pencil className="w-3 h-3" /></Button>
                        <DestructiveConfirmDialog
                          title="Xóa hệ số ngày?"
                          description={`Hệ số ngày ${fmtDate(o.work_date)} sẽ bị xóa.`}
                          confirmLabel="Xóa hệ số"
                          token={String(o.work_date?.slice(0, 10) || o.id)}
                          onConfirm={() => removeOverride(o.id)}
                          trigger={(
                            <Button size="sm" variant="outline" className="rounded-sm h-7 text-red-700"><Trash2 className="w-3 h-3" /></Button>
                          )}
                        />
                      </td>
                    </tr>
                  ))}
                  {overrides.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-stone-400">Chưa có hệ số ngày đặc biệt</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-stone-500">
              Hệ số ngày nhân với hệ số của khung ca. VD: ca chiều 100% × ngày Chủ nhật 200% = thực nhận 200%.
            </p>
          </TabsContent>
        </Tabs>
      </div>

      {/* Template dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editing?.id ? "Sửa khung ca" : "Khung ca mới"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Chi nhánh</Label>
                <Select value={editing.branch_id} onValueChange={(v) => setEditing({ ...editing, branch_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="sel-branch"><SelectValue placeholder="Chọn chi nhánh" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Tên ca</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="VD: Ca sáng, Ca đêm…" className="rounded-sm" data-testid="inp-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Giờ bắt đầu</Label>
                  <Input type="time" value={editing.start_time} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} className="rounded-sm" data-testid="inp-start" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Giờ kết thúc</Label>
                  <Input type="time" value={editing.end_time} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} className="rounded-sm" data-testid="inp-end" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Hệ số lương (%)</Label>
                  <Input type="number" value={editing.pay_multiplier} onChange={(e) => setEditing({ ...editing, pay_multiplier: e.target.value })} className="rounded-sm" data-testid="inp-mul" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Max slot</Label>
                  <Input type="number" value={editing.max_slots} onChange={(e) => setEditing({ ...editing, max_slots: e.target.value })} className="rounded-sm" data-testid="inp-slots" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Ngày trong tuần</Label>
                <div className="flex gap-1 mt-1">
                  {WEEKDAYS.map((w, i) => {
                    const on = !!(editing.weekday_mask & (1 << i));
                    return (
                      <button
                        key={w}
                        type="button"
                        onClick={() => {
                          const days = maskToDays(editing.weekday_mask);
                          days[i] = !on;
                          setEditing({ ...editing, weekday_mask: daysToMask(days) });
                        }}
                        className={`w-10 h-9 rounded-sm text-xs font-bold ${on ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-500"}`}
                        data-testid={`day-${i}`}
                      >{w}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Giới hạn role (tùy chọn)</Label>
                <Select value={editing.role_filter || "__any__"} onValueChange={(v) => setEditing({ ...editing, role_filter: v === "__any__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Mọi role</SelectItem>
                    {["restaurant", "kitchen", "warehouse", "sales", "manager"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="sw-active" />
                <Label>Đang dùng</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>Hủy</Button>
            <Button className="bg-emerald-900 text-white rounded-sm" onClick={saveTemplate} data-testid="btn-save-template">Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override dialog */}
      <Dialog open={!!ovEditing} onOpenChange={(o) => !o && setOvEditing(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl">Hệ số ngày đặc biệt</DialogTitle></DialogHeader>
          {ovEditing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Ngày</Label>
                <Input type="date" value={ovEditing.work_date?.slice(0, 10) || ""} onChange={(e) => setOvEditing({ ...ovEditing, work_date: e.target.value })} className="rounded-sm" data-testid="inp-ov-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Hệ số (%)</Label>
                <Input type="number" value={ovEditing.pay_multiplier} onChange={(e) => setOvEditing({ ...ovEditing, pay_multiplier: e.target.value })} className="rounded-sm" data-testid="inp-ov-mul" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Lý do</Label>
                <Input value={ovEditing.reason || ""} onChange={(e) => setOvEditing({ ...ovEditing, reason: e.target.value })} placeholder="VD: Chủ nhật, Tết, lễ 30/4…" className="rounded-sm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOvEditing(null)}>Hủy</Button>
            <Button className="bg-emerald-900 text-white rounded-sm" onClick={saveOverride} data-testid="btn-save-override">Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
